import Component from '@glimmer/component';
import { service } from '@ember/service';
import { VStack } from 'tio-ui/components/layout';
import { and, not } from 'tio-ui/utilities';
import { getSingleValueForTasField } from '../../../utils/tuition-assistance/fields.ts';
import ClaimsFinancialsCalculationsReadonlyCard from '../../tuition-assistance/program-details/claims-financials/calculations-readonly-card.gts';
import TuitionAssistanceApplicationDetailsClaimsFinancials from '../../tuition-assistance/application-details/courses-status-approval/claims-financials.gts';
import LimitsSummary from '../../tuition-assistance/limits-summary.gts';
import FinancialDetailsRequestSummary from './financial-details-request-summary.gts';
import FinancialDetailsApprovalSummary from './financial-details-approval-summary.gts';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type { TasClaimsFinancials } from '../../../types/tuition-assistance.ts';
import { doEmployeesExistInApproverList } from '../../../utils/tuition-assistance/approvers.ts';

import type SessionContextService from '../../../services/session-context.ts';
/**
 * Financial Details Content Component
 *
 * Displays and allows editing of financial totals for TAS applications.
 * This component contains all the financial summary logic, including:
 * - Claims financials (read-only and editable)
 * - Request summary (new and old formats)
 * - Approved totals with editing capabilities
 * - Course cash totals
 * - Limits summary and alerts
 *
 * NOTE: This component is for viewing/editing financial totals only,
 * NOT for approval actions (approve/reject/request changes).
 */
export interface FinancialDetailsContentSignature {
  Args: {
    application: TasApplicationModel;
    programInstance: TasProgramInstanceModel;
    // State from parent (minimal)
    editableApplicationTotal: number;
    editableCourseCashTotal: number;
    // Readonly view flag
    isReadonlyView: boolean;
    // Callbacks
    onUpdateApprovedTotal: (total: number, reason: string) => Promise<void>;
    onSaveClaimsFinancials: (financials: TasClaimsFinancials, reason: string) => Promise<void>;
    onUpdateClaimsFinancials: (financials: TasClaimsFinancials) => void;
    onSetApplicationTotal: (total: number) => void;
    onSetCourseCashTotal: (total: number) => void;
  };
  Element: HTMLElement;
}

export default class FinancialDetailsContent extends Component<FinancialDetailsContentSignature> {
  @service declare sessionContext: SessionContextService;

  getSingleValueForTasField = getSingleValueForTasField;

  // ========================================
  // Computed Properties (Component-Local)
  // ========================================

  get calculateTotalByCredit() {
    return this.args.programInstance.tasProgramTemplate.calculateTotalByCredit;
  }

  get calculateTotalByPercent() {
    return this.args.programInstance.tasProgramTemplate.calculateTotalByPercent;
  }

  get calculateTotalByClaimsFinancials() {
    return this.args.programInstance.tasProgramTemplate.calculateTotalByClaimsFinancials;
  }

  get calculateTotalByGPA() {
    return this.args.programInstance.tasProgramTemplate.calculateTotalByGPA;
  }

  // This excludes calculateTotalByCredit on purpose
  get isCalculatedApplicationTotal() {
    return this.calculateTotalByPercent || this.calculateTotalByGPA;
  }

  get isPendingApprovalAndNotReadonly() {
    if (this.args.isReadonlyView) {
      return false;
    }
    return !!this.args.application.state.includes('PENDING');
  }

  get coursesInAttendAndNotReadonly() {
    if (this.args.isReadonlyView) {
      return false;
    }
    const currentUser = this.sessionContext.user;

    return (
      this.args.application.state === 'TAS.ApplicationState.ATTEND' &&
      (this.isValidPossibleApproverForUpdateApprovedTotal ||
        currentUser?.isEmployeeAdmin ||
        currentUser?.isAccountOwner ||
        currentUser?.isTioAdmin ||
        currentUser?.isTasTioApprover)
    );
  }

  get hasApprovalPermissions() {
    const user = this.sessionContext.user;
    // TODO: The logic for determining employee admin or account owner roles
    // is slightly flawed and needs to be revisited.
    // Once we merge the session context service into tio-common, we can write a method to
    // determine if the user is an employeeAdmin/accountOwner of the company being viewed
    // rather than having any account owner role. (This is only an issue for tio-admin app)
    return Boolean(
      this.isValidPossibleApproverForUpdateApprovedTotal ||
      user?.isEmployeeAdmin ||
      user?.isAccountOwner ||
      user?.isTioAdmin ||
      user?.isTasTioApprover ||
      (this.args.application.isPrepaidProgram && user?.isPayOps)
    );
  }

  get isValidPossibleApproverForUpdateApprovedTotal() {
    const allPossibleApproverEmployeeIds =
      this.args.application.allPossibleApproverEmployeeIdsForAllWorkflows;
    const employees = this.sessionContext.user?.person?.employees || [];

    return doEmployeesExistInApproverList(employees, allPossibleApproverEmployeeIds);
  }

  get isAllowedEditApprovedTotalIfNotPendingApproval() {
    if (!this.args.application.isEditableApprovedTotal) {
      return false;
    }
    return this.hasApprovalPermissions;
  }

  get isProhibitedEdit() {
    return (
      !this.isPendingApprovalAndNotReadonly && !this.isAllowedEditApprovedTotalIfNotPendingApproval
    );
  }

  get fieldNameToEdit() {
    return this.args.application.fieldForEditableApprovedTotal;
  }

  get readonlyOriginalApplicationTotal() {
    return (
      (this.getSingleValueForTasField(
        this.fieldNameToEdit,
        this.args.application.fields
      ) as number) ??
      (this.args.application.approvedTotal as number) ??
      (this.args.application.requestedTotal as number)
    );
  }

  get readonlyCourseCashTuitionTotal() {
    return (
      (this.args.application.cardFinalApprovedTotal as number) ??
      (this.args.application.cardPreApprovedTotal as number) ??
      0
    );
  }

  get showNewRequestSummary() {
    // Also keep the payment-lifecycle view for a Course Cash app whose reservation has been
    // reduced to $0: shouldDisplayPaymentDetails goes false at all-zeros, which would otherwise
    // collapse the drawer back to the standard approval summary and surface a stale
    // CARD_FINAL_APPROVED_TOTAL. Keeping this view shows the (now $0) reservation card instead.
    return (
      this.args.application.shouldDisplayPaymentDetails ||
      this.args.application.hasCourseCashAssistance
    );
  }

  /**
   * Mixed-app case (e.g. Course Cash + reimbursement): the drawer shows the payment-lifecycle
   * request summary, but the reimbursement amount lives on the "field path" (an UNSETTLED
   * reimbursement assistance mirroring FINAL_APPROVED_TOTAL) and has no editor there. When true,
   * render the reimbursement approval summary (with a reimbursement-only Approved Total editor)
   * ABOVE the request summary. Field path only — gated by !hasLedgerContributingReimbursement so it
   * never appears when editing the total would instead create payment/repayment deltas, and gated
   * by hasReimbursementAssistance so it never shows a $0 reimbursement on a pure Course Cash app.
   */
  get showReimbursementEditor() {
    return (
      this.showNewRequestSummary &&
      !this.calculateTotalByClaimsFinancials &&
      this.args.application.hasReimbursementAssistance &&
      !this.args.application.hasLedgerContributingReimbursement &&
      this.args.application.isEditableApprovedTotal &&
      this.hasApprovalPermissions &&
      !this.isProhibitedEdit
    );
  }

  get paymentMethodIsCourseCash() {
    return this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  <template>
    <LimitsSummary
      @application={{@application}}
      @employee={{@programInstance.employee}}
      @approvedTotalToValidate={{@editableApplicationTotal}}
      @approvedCourseCashTotalToValidate={{@editableCourseCashTotal}}
      @labelClass="col-span-5"
      @valueClass="col-span-2 app-status-text-brand"
      class="tio-col-flush h-full"
      ...attributes
      as |Summary|
    >
      <VStack class="tio-col-flush h-full">
        {{! Claims Financials OR Request Summary section }}
        {{#if this.calculateTotalByClaimsFinancials}}
          {{#if @isReadonlyView}}
            <ClaimsFinancialsCalculationsReadonlyCard
              @claimsFinancialsFields={{@application.claimsFinancialsCalculatorInput}}
              @showDetails={{true}}
              class="my-4"
            />
          {{else}}
            <TuitionAssistanceApplicationDetailsClaimsFinancials
              @claimsFinancials={{@application.claimsFinancialsCalculatorInput}}
              @isPendingApprovalAndNotReadonly={{this.isPendingApprovalAndNotReadonly}}
              @didSave={{@onSaveClaimsFinancials}}
              @didUpdate={{@onUpdateClaimsFinancials}}
              @applicationState={{@application.state}}
              class="my-4"
            />

          {{/if}}
        {{else}}

          {{#if this.showNewRequestSummary}}
            {{#if this.showReimbursementEditor}}
              {{! Mixed app (Course Cash + reimbursement): the reimbursement amount lives on the }}
              {{! field path with no editor in the request summary, so render the reimbursement }}
              {{! approval summary (reimbursement-only editable total) above the Course Cash cards. }}
              <FinancialDetailsApprovalSummary
                @application={{@application}}
                @programInstance={{@programInstance}}
                @isCalculatedApplicationTotal={{this.isCalculatedApplicationTotal}}
                @readonlyOriginalApplicationTotal={{this.readonlyOriginalApplicationTotal}}
                @readonlyCourseCashTuitionTotal={{this.readonlyCourseCashTuitionTotal}}
                @updateAndSaveApprovedTotalWithComment={{@onUpdateApprovedTotal}}
                @setApplicationTotal={{@onSetApplicationTotal}}
                @setCourseCashTotal={{@onSetCourseCashTotal}}
                @isProhibitedEdit={{this.isProhibitedEdit}}
                @isPendingApprovalAndNotReadonly={{this.isPendingApprovalAndNotReadonly}}
                @isAllowedEditApprovedTotalIfNotPendingApproval={{this.isAllowedEditApprovedTotalIfNotPendingApproval}}
                @isSubmitting={{false}}
                @reimbursementOnly={{true}}
              >
                <:warning>
                  <Summary.LimitsAlertForApproverIfApplicable class="text-sm flex mt-2" />
                </:warning>
              </FinancialDetailsApprovalSummary>
            {{/if}}
            <FinancialDetailsRequestSummary
              @application={{@application}}
              @isProhibitedEdit={{and this.isProhibitedEdit (not this.hasApprovalPermissions)}}
              class="h-full"
            />
          {{else}}
            {{! Approval Summary - shown when there's an approved total but not yet in payment stage }}
            <FinancialDetailsApprovalSummary
              @application={{@application}}
              @programInstance={{@programInstance}}
              @isCalculatedApplicationTotal={{this.isCalculatedApplicationTotal}}
              @readonlyOriginalApplicationTotal={{this.readonlyOriginalApplicationTotal}}
              @readonlyCourseCashTuitionTotal={{this.readonlyCourseCashTuitionTotal}}
              @updateAndSaveApprovedTotalWithComment={{@onUpdateApprovedTotal}}
              @setApplicationTotal={{@onSetApplicationTotal}}
              @setCourseCashTotal={{@onSetCourseCashTotal}}
              @isProhibitedEdit={{this.isProhibitedEdit}}
              @isPendingApprovalAndNotReadonly={{this.isPendingApprovalAndNotReadonly}}
              @isAllowedEditApprovedTotalIfNotPendingApproval={{this.isAllowedEditApprovedTotalIfNotPendingApproval}}
              @isSubmitting={{false}}
            >
              <:warning>
                <Summary.LimitsAlertForApproverIfApplicable class="text-sm flex mt-2" />
              </:warning>
            </FinancialDetailsApprovalSummary>
          {{/if}}
        {{/if}}
      </VStack>
    </LimitsSummary>
  </template>
}
